import React, { useEffect, useState } from 'react';
import fr from 'facet-rest';
import {
  useManagedAccountPageContext,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Context/ManagedAccountPageContext';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { FormStepCollectionProvider } from 'Components/UI/Accounts/AccountApplication/AccountApplicationContext';
import AccountApplicationContent from 'Components/Modals/AccountApplication/AccountApplicationContent';
import AccountApplicationContainer from 'Components/UI/Accounts/AccountApplication/AccountApplicationContainer';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getSectionCardStyle } from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/styles';
import { useSnackbarContext } from 'Context/SnackbarContext';

const useStyles = buildUseStyles('ManagedAccountInfoTabPanelLayout', (theme) => ({
  root: {
    ...getSectionCardStyle({ theme }),
  },
}));

async function fetchManagedAccountInfoTabData(managedAccountPage) {
  const managedAccountCollection = await managedAccountPage.relationship(fr.relationships.managedAccount.name)?.link('related').get();
  const managedAccount = managedAccountCollection?.resources().head();

  const accountDetailsCollection = await managedAccount?.relationship(fr.relationships.accountDetails.name)?.link('related').get();
  const accountDetails = accountDetailsCollection?.resources().head();

  const accountApplicationLinkURL = await accountDetails?.relationship(fr.relationships.accountApplication.name)?.link('related').url();

  return {
    accountApplicationLinkURL,
  };
}

function useManagedAccountInfo(managedAccountPage) {
  const { displayErrorMessage } = useSnackbarContext();

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(null);
  const [accountApplicationLinkURL, setAccountApplicationLinkURL] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async function fetchData() {
      setIsLoading(true);
      setIsError(null);

      try {
        const {
          accountApplicationLinkURL: applicationLinkURL,
        } = await fetchManagedAccountInfoTabData(managedAccountPage);

        if (!ignore) {
          setAccountApplicationLinkURL(applicationLinkURL);
        }
      } catch (e) {
        setIsError(e || true);
        displayErrorMessage(e, (e.errors && e.errors[0]?.detail) || 'Unable to fetch account information');
      }

      setIsLoading(false);
    }());

    return () => {
      ignore = true;
    };
  }, [managedAccountPage]);

  return {
    isLoading,
    isError,
    accountApplicationLinkURL,
  };
}

export default function ManagedAccountInfoTabPanelLayout() {
  const classes = useStyles();

  const { managedAccountPageResource } = useManagedAccountPageContext();
  const {
    isLoading,
    isError,
    accountApplicationLinkURL,
  } = useManagedAccountInfo(managedAccountPageResource);

  if (isError) {
    return <Box><Subtitle value="Could not load the information. Please retry later." /></Box>;
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Box className={classes.root}>
      <FormStepCollectionProvider initialState={{ editMode: true }}>
        <AccountApplicationContainer link={accountApplicationLinkURL}>
          <AccountApplicationContent />
        </AccountApplicationContainer>
      </FormStepCollectionProvider>
    </Box>
  );
}
